// 전체코드
// src/pages/course/AddingCoursePage.js

import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import CourseMapView from "../../components/course/CourseMapView";
import CourseRunningStatus from "../../components/course/CourseRunningStatus";
import {
  endCourseApi,
  getLastMeasurement,
  giveupCourse,
} from "../../api/courseApi";
import { useToast } from "../../contexts/ToastContext";
import { useJWT } from "../../contexts/JWTContext";

const DRAFT_KEY_PREFIX = "wingstep_course_adding_draft";

function getDraftKey(userId) {
  return `${DRAFT_KEY_PREFIX}:${userId || "unknown"}`;
}

function readDraft(userId) {
  try {
    const raw = localStorage.getItem(getDraftKey(userId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function clearDraft(userId) {
  try {
    localStorage.removeItem(getDraftKey(userId));
  } catch (e) {}
}

function parsePointWkt(pointWkt) {
  if (!pointWkt || typeof pointWkt !== "string") return null;

  const match = pointWkt.match(/POINT\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i);
  if (!match) return null;

  const first = Number(match[1]);
  const second = Number(match[2]);
  if (Number.isNaN(first) || Number.isNaN(second)) return null;

  return { first, second };
}

// 카카오 JS SDK(services)로 좌표 -> 주소
function coordToAddressByKakaoJs(lng, lat) {
  return new Promise((resolve, reject) => {
    if (!window.kakao || !window.kakao.maps || !window.kakao.maps.services) {
      reject(new Error("Kakao Maps services 로드 실패"));
      return;
    }

    const geocoder = new window.kakao.maps.services.Geocoder();
    geocoder.coord2Address(lng, lat, (result, status) => {
      const OK = window.kakao.maps.services.Status.OK;
      if (status !== OK || !result || !result[0]) {
        reject(new Error("주소 변환 실패"));
        return;
      }

      const road = result[0].road_address?.address_name;
      const jibun = result[0].address?.address_name;
      resolve(road || jibun || "");
    });
  });
}

function AddingCoursePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { userId: loginUserId } = useJWT();

  // state는 언제든 날아갈 수 있으므로, state -> localStorage 순으로 복구
  const initState = location.state || {};
  const [workRecordId, setWorkRecordId] = useState(
    initState.workRecordId ?? null
  );
  const [type, setType] = useState(initState.type ?? null);
  const [courseName, setCourseName] = useState(initState.courseName ?? null);

  // 출발지 1회만 고정하기 위한 잠금
  const startLockedRef = useRef(false);

  // 종료 요청 중복 방지
  const submittingRef = useRef(false);

  // localStorage 소실 시 giveup을 무한 호출하지 않도록 1회만
  const cleanupOnceRef = useRef(false);

  // 1) 진입/복구 로직: state가 비면 localStorage에서 복구
  useEffect(() => {
    if (!loginUserId) {
      showToast("로그인 정보가 없습니다. 다시 시도해주세요.", "e");
      navigate("/course/add", { replace: true });
      return;
    }

    const draft = readDraft(loginUserId);

    // workRecordId 복구
    if (!workRecordId) {
      const draftWorkRecordId = draft?.workRecordId ?? null;
      if (!draftWorkRecordId) {
        showToast("코스 생성 정보가 없습니다. 다시 시도해주세요.", "e");
        navigate("/course/add", { replace: true });
        return;
      }
      setWorkRecordId(draftWorkRecordId);
    }

    // type/courseName 복구 (workRecordId가 맞는 draft만 인정)
    const effectiveWorkRecordId = workRecordId || draft?.workRecordId;

    if ((!type || !courseName) && effectiveWorkRecordId) {
      const okDraft =
        draft &&
        Number(draft.workRecordId) === Number(effectiveWorkRecordId) &&
        String(draft.type || "").trim() &&
        String(draft.courseName || "").trim();

      if (okDraft) {
        if (!type) setType(draft.type);
        if (!courseName) setCourseName(draft.courseName);
        return;
      }

      // draft가 없거나 불일치면 복구 불가
      // 이 경우는 측정만 남아있을 수 있으니 1회만 강제 종료하고 내보냄
      if (!cleanupOnceRef.current) {
        cleanupOnceRef.current = true;
        showToast("데이터가 소실되어 복구가 불가능합니다", "w");

        Promise.resolve()
          .then(() => giveupCourse(effectiveWorkRecordId))
          .catch((e) => console.error("생성중 강제종료 실패:", e))
          .finally(() => {
            clearDraft(loginUserId);
            navigate("/course/add", { replace: true });
          });
      }
    }
  }, [loginUserId, workRecordId, type, courseName, navigate, showToast]);

  const [status, setStatus] = useState({
    distance: 0,
    time: "00:00:00",
    speed: 0,
    heartrate: 0,
    calorie: 0,
    startLocationWkt: null,
    startAddressText: "",
    lastLocationWkt: null,
  });

  // 2) 측정값 폴링
  useEffect(() => {
    if (!workRecordId) return;

    const fetchMeasurement = async () => {
      try {
        const data = await getLastMeasurement(workRecordId);
        if (!data) return;

        const locationWkt =
          data.measurementLocation ??
          data.locationWkt ??
          data.rawLocationWkt ??
          data.location ??
          null;

        setStatus((prev) => ({
          ...prev,
          distance: data.distance ?? prev.distance,
          time: data.time ?? prev.time,
          speed: data.speed ?? prev.speed,
          heartrate: data.heartrate ?? prev.heartrate,
          calorie: data.calorie ?? prev.calorie,
          lastLocationWkt: locationWkt ?? prev.lastLocationWkt,
        }));

        if (!startLockedRef.current && locationWkt) {
          const parsed = parsePointWkt(locationWkt);
          if (!parsed) return;

          // 너희 저장 형식이 POINT(lat lng) 기준이라고 했으니 유지
          const lat = parsed.first;
          const lng = parsed.second;

          startLockedRef.current = true;

          setStatus((prev) => ({
            ...prev,
            startLocationWkt: locationWkt,
          }));

          try {
            const addr = await coordToAddressByKakaoJs(lng, lat);
            setStatus((prev) => ({
              ...prev,
              startAddressText: addr,
            }));
          } catch (e) {
            console.error("출발지 주소 변환 실패:", e);
          }
        }
      } catch (error) {
        console.error("측정값 조회 실패:", error);
      }
    };

    fetchMeasurement();
    const intervalId = setInterval(fetchMeasurement, 5000);
    return () => clearInterval(intervalId);
  }, [workRecordId]);

  // 3) 코스 종료
  const handleEndCourse = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;

    try {
      if (!window.confirm("코스를 종료하시겠습니까?")) return;

      if (!loginUserId || !workRecordId || !type || !courseName) {
        showToast("코스 생성 정보가 올바르지 않습니다.", "e");
        return;
      }

      const savedCourse = await endCourseApi(
        loginUserId,
        workRecordId,
        courseName,
        type,
        status.startAddressText
      );

      // 생성 완료되면 draft 제거
      clearDraft(loginUserId);

      showToast("코스가 생성되었습니다.", "i");
      navigate(`/course/${savedCourse.courseId}`, { replace: true });
    } catch (error) {
      showToast("코스는 3km 이상이어야 합니다.", "w");
      navigate("/course");
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex flex-col max-w-md mx-auto relative font-sans">
      <header className="px-5 pt-6 pb-4 bg-white sticky top-0 z-20 shadow-sm flex items-center justify-center">
        <h1 className="text-xl font-bold text-gray-800">코스 생성 중...</h1>
      </header>

      <main className="flex-1 overflow-y-auto px-5 py-6 pb-20 scrollbar-hide space-y-6">
        <section className="bg-white rounded-[24px] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 h-64 relative">
          <CourseMapView />
          <div className="absolute top-3 left-3 z-10 bg-white/90 px-3 py-1.5 rounded-xl shadow-sm border border-gray-200">
            <span className="text-xs font-bold text-red-500">기록 중</span>
          </div>

          {status.startAddressText ? (
            <div className="absolute bottom-3 left-3 right-3 z-10 bg-white/90 px-3 py-2 rounded-xl shadow-sm border border-gray-200">
              <div className="text-xs font-semibold text-gray-700 truncate">
                출발지: {status.startAddressText}
              </div>
            </div>
          ) : null}
        </section>

        <CourseRunningStatus
          type={type || "코스"}
          distance={status.distance}
          time={status.time}
          speed={status.speed}
          heartRate={status.heartrate}
          calorie={status.calorie}
          onEndCourse={handleEndCourse}
          buttonText="코스 종료"
        />
      </main>
    </div>
  );
}

export default AddingCoursePage;
